import { EventType, useI18N } from "@sundae/react-hooks";
import { Text, Tooltip, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, useCallback } from "react";

import { StarIcon as StarOutlinedIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { useEventTracking } from "../../hooks/useEventTracking";
import { PersistentActions, useProfileAppContext } from "../../stores";

export interface IFavoriteIndicatorProps {
  ident: string;
}

const StarFavoritePoolIndicator: FC<IFavoriteIndicatorProps> = ({ ident }) => {
  const { t } = useI18N("liquidity");
  const {
    dispatch,
    state: { favoritePools },
  } = useProfileAppContext();
  const handleEventTracking = useEventTracking();
  const isFavorite = favoritePools.includes(ident);
  const Icon = isFavorite ? StarSolidIcon : StarOutlinedIcon;

  const handleTrackPool = useCallback(
    () =>
      handleEventTracking({
        eventType: EventType.Click,
        source: "StarredPoolIndicator",
        value: `${isFavorite ? "remove_from" : "add_to"}_favorites_${ident}`,
      }),
    [ident, isFavorite]
  );

  const handleClick = useCallback(() => {
    PersistentActions.filters.pools[isFavorite ? "removeFavoritePool" : "addFavoritePool"](
      ident,
      dispatch
    );
    handleTrackPool();
  }, [ident, isFavorite]);

  return (
    <Tooltip
      trigger={
        <Text variant="warning" tag="span">
          <Icon
            className={cx(
              "h-4 w-4 cursor-pointer",
              toolkitCx.layout.transition.transform,
              toolkitCx.layout.text.warning,
              toolkitCx.recipes.scaleWhenActive
            )}
            onClick={handleClick}
          />
        </Text>
      }
      tooltipLabel={t("pools.starred", { context: isFavorite ? "remove" : "add" })}
    />
  );
};

export default StarFavoritePoolIndicator;
